import {useApolloClient, useLazyQuery, useMutation, useQuery} from '@apollo/client'
import {GET_ALL_QUOTES} from '../../gql/queries/quotesQuery'
import {IResQuotes} from './quotesTypes'
import {useCallback, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {Badge, Breadcrumb, Button, ButtonGroup, Col, Dropdown, Form, InputGroup, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {Tooltip} from 'react-tooltip'
import {InfoClientPolicy} from '../policies/components/InfoClientPolicy'
import {hasPermission, parseId} from '../../helpers'
import moment from 'moment'
import {useDisclourse} from '../../hooks/useDisclourse'
import {ModalPreview} from '../policies/components/ModalPreview'
import {NodePolicy} from '../../types/policiesApi'
import {PaginationUtils} from '../shared/PaginationUtils'
import {
  SIZE_PAGE_DEFAULT,
  INITIAL_POLICY_FILTERS,
  IMMIGRATION_STATUS_CHOICES,
  DOCUMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  ALL_PERMISSIONS,
} from '../../helpers/consts'
import {
  metalLevelLabel,
  translatedDocumentStatuses,
  translatedImmigrationStatuses,
  translatedMetalLevels,
  translatedPaymentStatuses,
} from '../../helpers/catalogMessages'
import {ModalAddPolice} from '../policies/components/ModalAddPolice'
import {ModalFilters} from '../policies/components/ModalFilters'
import {PolicyFilters, PolicySavedFilter} from '../policies/policiesTypes'
import {buildPolicyExportParams} from '../policies/policyExportParams'
import {URL} from '../../gql/client'
import {GET_ALL_POLICIES_STATUSES} from '../../gql/queries/policiesQueries'
import {GET_ALL_CARRIERS_LIST} from '../../gql/queries/carriersQuery'
import {GET_ALL_SIGNATURE_FORMS_ACTIVE} from '../../gql/queries/signatureFormsApiQueries'
import {GET_ALL_STATES_EU_LIST} from '../../gql/queries/statesEUQuery'
import {GET_ASSIGNMENT_USERS_LIST} from '../../gql/queries/usersQuery'
import {GET_ALL_POLICY_TYPE_CATEGORIES} from '../../gql/queries/policyTypesQuery'
import {GET_ALL_LEAD_SOURCES} from '../../gql/queries/leadSourceQuery'
import {PaginationSection} from '../shared/PaginationSection'
import {IResUpdatePolicyApi} from '../../types/policies'
import {AgentInfoTooltip} from '../shared/AgentInfoTooltip'
import {UserAvatar} from '../../components/UserAvatar'
import {EDIT_POLICY} from '../../gql/mutations/policyMutation'

import {AppliedFiltersList} from '../policies/components/AppliedFiltersList'
import { GET_ALL_USERS_FILTER } from '../../gql/queries/usersQuery'
import { IResAllUsers } from '../oneUser/oneUserTypes'
import {ModalNotes} from '../onePolicy/components/ModalNotes'
import {ModalAssignAgentPolicy} from '../onePolicy/components/ModalAssignAgentPolicy'
import {ModalViewLogs} from '../policies/components/ModalViewLogs'
import {GET_MY_POLICY_FILTERS} from '../../gql/queries/policyFiltersQueries'
import {
  CREATE_POLICY_FILTER,
  DELETE_POLICY_FILTER,
  UPDATE_POLICY_FILTER,
} from '../../gql/mutations/policyFilterMutations'
import {useGetUser} from '../../store/userStore'
import {useStaticCatalog} from '../../hooks/useStaticCatalog'

// Hook para persistir en localStorage
function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch {
      return initialValue
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue))
    } catch {
      // puede fallar en modo incógnito
    }
  }, [key, storedValue])

  return [storedValue, setStoredValue] as const
}

// Hook convertido a lazy loading selectivo - solo carga las queries necesarias
interface LoadFilterOptions {
  loadUsers?: boolean
  loadAgents?: boolean
  loadSignatureForms?: boolean
  loadCarriers?: boolean
  loadStates?: boolean
  loadPolicyTypes?: boolean
  loadLeadSources?: boolean
  unrestrictedAssignmentUsers?: boolean
}

export const useLoadFilterData = (options: LoadFilterOptions) => {
  const intl = useIntl()
  const client = useApolloClient()

  const fetchUsers = useCallback(
    () =>
      client
        .query({
          query: GET_ASSIGNMENT_USERS_LIST,
          variables: {assignmentCandidates: !options.unrestrictedAssignmentUsers},
          fetchPolicy: 'network-only',
        })
        .then((res) => res.data),
    [client, options.unrestrictedAssignmentUsers]
  )
  const usersCatalog = useStaticCatalog(
    options.unrestrictedAssignmentUsers ? 'all-users-list-unrestricted' : 'all-users-list',
    !!options.loadUsers,
    fetchUsers
  )

  const fetchAgents = useCallback(
    () =>
      client
        .query<IResAllUsers>({
          query: GET_ALL_USERS_FILTER,
          variables: {requireNpn: true},
          fetchPolicy: 'network-only',
        })
        .then((res) => res.data),
    [client]
  )
  const agentsCatalog = useStaticCatalog('agents-with-npn', !!options.loadAgents, fetchAgents)

  const fetchSignatureForms = useCallback(
    () =>
      client
        .query({query: GET_ALL_SIGNATURE_FORMS_ACTIVE, fetchPolicy: 'network-only'})
        .then((res) => res.data),
    [client]
  )
  const signatureFormsCatalog = useStaticCatalog(
    'signature-forms-active',
    !!options.loadSignatureForms,
    fetchSignatureForms
  )

  const fetchCarriers = useCallback(
    () =>
      client
        .query({query: GET_ALL_CARRIERS_LIST, fetchPolicy: 'network-only'})
        .then((res) => res.data),
    [client]
  )
  const carriersCatalog = useStaticCatalog('carriers-list', !!options.loadCarriers, fetchCarriers)

  const fetchPolicyStatuses = useCallback(
    () =>
      client
        .query({query: GET_ALL_POLICIES_STATUSES, fetchPolicy: 'network-only'})
        .then((res) => res.data),
    [client]
  )
  const policyStatusesCatalog = useStaticCatalog(
    'policy-statuses:all',
    !!options.loadPolicyTypes,
    fetchPolicyStatuses
  )

  const fetchStates = useCallback(
    () =>
      client
        .query({query: GET_ALL_STATES_EU_LIST, fetchPolicy: 'network-only'})
        .then((res) => res.data),
    [client]
  )
  const statesCatalog = useStaticCatalog('states-eu', !!options.loadStates, fetchStates)

  const fetchPolicyTypes = useCallback(
    () =>
      client
        .query({query: GET_ALL_POLICY_TYPE_CATEGORIES, fetchPolicy: 'network-only'})
        .then((res) => res.data),
    [client]
  )
  const policyTypesCatalog = useStaticCatalog(
    'policy-type-categories',
    !!options.loadPolicyTypes,
    fetchPolicyTypes
  )

  const fetchLeadSources = useCallback(
    () =>
      client
        .query({
          query: GET_ALL_LEAD_SOURCES,
          variables: {category: 'Policy', enabled: true},
          fetchPolicy: 'network-only',
        })
        .then((res) => res.data),
    [client]
  )
  const leadSourcesCatalog = useStaticCatalog(
    'lead-sources-policy',
    !!options.loadLeadSources,
    fetchLeadSources
  )

  if (usersCatalog.error) toast.error(intl.formatMessage({id: 'quotes.page.toast.errorUsers', defaultMessage: 'Error loading users'}))
  if (agentsCatalog.error) toast.error(intl.formatMessage({id: 'quotes.page.toast.errorAgents', defaultMessage: 'Error loading agents (users with npn)'}))
  if (signatureFormsCatalog.error) toast.error(intl.formatMessage({id: 'quotes.page.toast.errorSignatureForms', defaultMessage: 'Error loading signature forms'}))
  if (carriersCatalog.error) toast.error(intl.formatMessage({id: 'quotes.page.toast.errorCarriers', defaultMessage: 'Error loading carriers'}))
  if (policyStatusesCatalog.error) toast.error(intl.formatMessage({id: 'quotes.page.toast.errorPolicyStatuses', defaultMessage: 'Error loading policy statuses'}))
  if (statesCatalog.error) toast.error(intl.formatMessage({id: 'quotes.page.toast.errorStates', defaultMessage: 'Error loading states'}))
  if (policyTypesCatalog.error) toast.error(intl.formatMessage({id: 'quotes.page.toast.errorPolicyTypes', defaultMessage: 'Error loading policy types'}))
  if (leadSourcesCatalog.error) toast.error(intl.formatMessage({id: 'quotes.page.toast.errorLeadSources', defaultMessage: 'Error loading lead sources'}))

  return {
    dataUsers: usersCatalog.data,
    dataAgents: agentsCatalog.data,
    dataSignatureForms: signatureFormsCatalog.data,
    dataCarriers: carriersCatalog.data,
    dataPolicyStatuses: policyStatusesCatalog.data,
    dataStates: statesCatalog.data,
    dataPolicyTypes: policyTypesCatalog.data,
    dataLeadSources: leadSourcesCatalog.data,
  }
}

export const QuotesPage = () => {
  const intl = useIntl()
  const space = ' '
  const notRecorded = 'N/R'
  const dollar = '$'
  const user = useGetUser((s) => s.user)
  const permissions = useGetUser((s) => s.user?.permissions) || []
  const canViewAllPolicies =
    permissions && hasPermission(permissions, ALL_PERMISSIONS.VIEW_ALL_POLICIES)
  const canExportQuote = permissions && hasPermission(permissions, ALL_PERMISSIONS.EXPORT_POLICY)

  const [gqlGetPolicies, {data: dataAllQuotes, loading, error, refetch}] = useLazyQuery<IResQuotes>(
    GET_ALL_QUOTES,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [gqlUpdateQuote, {data: dataUpdate}] = useMutation<IResUpdatePolicyApi>(EDIT_POLICY)
  const {data: dataPolicyFilters, refetch: refetchPolicyFilters} = useQuery<{
    myPolicyFilters: {
      id: string
      name: string
      scope?: string
      filters: string | PolicyFilters
      created?: string
      updated?: string
    }[]
  }>(GET_MY_POLICY_FILTERS, {
    fetchPolicy: 'network-only',
    variables: {scope: 'QUOTE'},
  })
  const [gqlCreatePolicyFilter] = useMutation(CREATE_POLICY_FILTER)
  const [gqlUpdatePolicyFilter] = useMutation(UPDATE_POLICY_FILTER)
  const [gqlDeletePolicyFilter] = useMutation(DELETE_POLICY_FILTER)

  const [formData, setFormData] = useLocalStorage<PolicyFilters>(
    'quotes-filters',
    INITIAL_POLICY_FILTERS
  )
  const {isOpen: isOpenFilters, onOpen: onOpenFilters, onClose: onCloseFilters} = useDisclourse()

  const parsePolicyFilterPayload = (rawFilters: string | PolicyFilters | undefined) => {
    if (!rawFilters) return INITIAL_POLICY_FILTERS
    if (typeof rawFilters === 'string') {
      try {
        const parsed = JSON.parse(rawFilters) as PolicyFilters
        return {...INITIAL_POLICY_FILTERS, ...parsed}
      } catch {
        return INITIAL_POLICY_FILTERS
      }
    }
    return {...INITIAL_POLICY_FILTERS, ...rawFilters}
  }

  const savedPolicyFilters: PolicySavedFilter[] =
    dataPolicyFilters?.myPolicyFilters?.map((filter) => ({
      id: filter.id,
      name: filter.name,
      created: filter.created,
      updated: filter.updated,
      filters: parsePolicyFilterPayload(filter.filters),
    })) || []

  // Función para determinar qué queries cargar basándose en los filtros aplicados
  const getFilterLoadOptions = (filters: PolicyFilters): LoadFilterOptions => {
    const getLength = (value: unknown) => {
      if (Array.isArray(value)) return value.length
      if (typeof value === 'string') return value.length
      return 0
    }

    return {
      loadUsers: getLength(filters.assignedTo) > 0,
      loadAgents: getLength(filters.user) > 0,
      loadSignatureForms: !!filters.signatureForm,
      loadCarriers: getLength(filters.carrier) > 0,
      loadStates: getLength(filters.stateEU) > 0,
      loadPolicyTypes: getLength(filters.policyType) > 0,
      loadLeadSources: getLength(filters.leadSource) > 0,
    }
  }

  // Detectar si hay filtros aplicados y determinar qué cargar
  const initialFilterOptions = getFilterLoadOptions(formData)
  const hasAnyFilterToLoad = Object.values(initialFilterOptions).some((v) => v === true)

  // Estado para controlar qué datos de filtros cargar
  // Arranca vacío para no competir con los queries críticos (lista + totales).
  // Se precarga en background después de 1.5s para que el modal abra instantáneo.
  const [filterLoadOptions, setFilterLoadOptions] = useState<LoadFilterOptions>(
    hasAnyFilterToLoad ? initialFilterOptions : {}
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilterLoadOptions({
        loadUsers: true,
        loadAgents: true,
        loadSignatureForms: true,
        loadCarriers: true,
        loadStates: true,
        loadPolicyTypes: true,
        loadLeadSources: true,
      })
    }, 1500)
    return () => clearTimeout(timer)
  }, [])

  const {
    dataUsers,
    dataAgents,
    dataSignatureForms,
    dataCarriers,
    dataPolicyStatuses,
    dataStates,
    dataLeadSources,
    dataPolicyTypes,
  } = useLoadFilterData({
    ...filterLoadOptions,
    unrestrictedAssignmentUsers: canViewAllPolicies,
  })

  const filterData = {
    users:
      dataUsers?.allUsersList?.map((u) => ({
        ...u,
        fullName: `${u.firstName} ${u.lastName}`,
      })) || [],
    agents:
      dataAgents?.allUsers.edges.map(({node: u}) => ({
        ...u,
        fullName: `${u.firstName} ${u.lastName} - ${u.email} (${u.npn} )`,
      })) || [],
    states: dataStates?.allStatesList || [],
    signatureForms: dataSignatureForms?.allSignatureForms?.edges.map(({node}) => node) || [],
    carriers: dataCarriers?.allInsuranceOperatorsList || [],
    policyStatuses:
      dataPolicyStatuses?.allPolicyStatuses?.edges.map(({node}) => ({
        id: node.id,
        status: node.code,
      })) || [],
    immigrationStatuses: IMMIGRATION_STATUS_CHOICES,
    leadSources: dataLeadSources?.allLeadSources?.edges.map(({node}) => node) || [],
    policyTypes: dataPolicyTypes?.allCategoryPolicy?.edges.map(({node}) => node) || [],
    documentStatuses: DOCUMENT_STATUS_OPTIONS,
    paymentStatuses: PAYMENT_STATUS_OPTIONS

  }

  const {isOpen, onClose, onOpen} = useDisclourse()
  const {isOpen: isOpenPreview, onClose: onClosePreview, onOpen: onOpenPreview} = useDisclourse()
  const {isOpen: isOpenLogs, onOpen: onOpenLogs, onClose: onCloseLogs} = useDisclourse()
  const {isOpen: isOpenNotes, onOpen: onOpenNotes, onClose: onCloseNotes} = useDisclourse()
  const {
    isOpen: isOpenAssignPolicy,
    onOpen: onOpenAssignPolicy,
    onClose: onCloseAssignPolicy,
  } = useDisclourse()

  const [infoPreview, setInfoPreview] = useState<null | NodePolicy>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [offset, setOffset] = useState(0)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [page, setPage] = useState(1)

  const [dataArr, setDataArr] = useState<IResQuotes['allQuotes']['edges']>([])

  let totalContacts = dataAllQuotes?.allQuotes?.totalCount ?? 0

  const totalPages = Math.ceil(totalContacts / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalContacts)

  const currentYear = new Date().getFullYear()
  const lastThreeYears = [currentYear - 1, currentYear, currentYear + 1]

  const [searchByMembers, setSearchByMembers] = useState(false)
  const [selectedYears, setSelectedYears] = useState<number[]>([])
  const [loadingExport, setLoadingExport] = useState(false)

  const handleDownload = async () => {
    if (!user) return
    const {token} = user
    if (!token) return

    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${token}`)

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
    }

    // La página de quotes no tiene tabs (Important/Archived/Reminders), por eso va vacío.
    const params = buildPolicyExportParams(formData, selectedYears, '', searchByMembers)

    setLoadingExport(true)

    try {
      const res = await fetch(`${URL}/export-quotes-quick-excel?${params.toString()}`, requestOptions)
      if (!res.ok) throw new Error(intl.formatMessage({id: 'quotes.page.exportError', defaultMessage: 'Error exporting'}))
      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `quotes-${moment().format('DD-MM-YY')}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: (err as {message: string}).message}))
    } finally {
      setLoadingExport(false)
    }
  }

  // Estado para el ordenamiento
  type SortColumn = 'agent' | 'client' | 'carrier' | 'status' | 'effectiveDate' | 'assignedTo' | 'created' | null
  type SortDirection = 'asc' | 'desc'
  const [sortColumn, setSortColumn] = useState<SortColumn>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Mapeo de columnas frontend a backend
  const columnToBackendMap: Record<Exclude<SortColumn, null>, string> = {
    agent: 'user',
    client: 'holder',
    carrier: 'carrier',
    status: 'status',
    created: 'created',
    effectiveDate: 'effectiveDate',
    assignedTo: 'assignedTo',
  }

  const cleanVariables = (vars: any) => {
    return Object.fromEntries(
      Object.entries(vars).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    )
  }

  const handlePolicyFilterErrors = (
    errors: {field?: string; message?: string}[] | undefined,
    fallback: string
  ) => {
    const message = errors?.[0]?.message || fallback
    toast.error(message)
  }

  const handleSavePolicyFilter = async (name: string, filters: PolicyFilters) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error(intl.formatMessage({id: 'quotes.page.toast.filterNameRequired', defaultMessage: 'Filter name is required.'}))
      return null
    }

    const result = await gqlCreatePolicyFilter({
      variables: {
        policyFilterData: {
          name: trimmedName,
          scope: 'QUOTE',
          filters: JSON.stringify(filters),
        },
      },
    })

    const payload = result.data?.createPolicyFilter
    if (!payload?.success) {
      handlePolicyFilterErrors(payload?.errors, intl.formatMessage({id: 'quotes.page.toast.unableSaveFilter', defaultMessage: 'Unable to save filter.'}))
      return null
    }

    await refetchPolicyFilters()
    toast.success(intl.formatMessage({id: 'quotes.page.toast.filterSaved', defaultMessage: 'Filter saved.'}))

    if (!payload.policyFilter) return null
    return {
      id: payload.policyFilter.id,
      name: payload.policyFilter.name,
      filters: parsePolicyFilterPayload(payload.policyFilter.filters),
      updated: payload.policyFilter.updated,
    } as PolicySavedFilter
  }

  const handleUpdatePolicyFilter = async (id: string, name: string, filters: PolicyFilters) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error(intl.formatMessage({id: 'quotes.page.toast.filterNameRequired', defaultMessage: 'Filter name is required.'}))
      return null
    }

    const result = await gqlUpdatePolicyFilter({
      variables: {
        id: parseId(id),
        policyFilterData: {
          name: trimmedName,
          scope: 'QUOTE',
          filters: JSON.stringify(filters),
        },
      },
    })

    const payload = result.data?.updatePolicyFilter
    if (!payload?.success) {
      handlePolicyFilterErrors(payload?.errors, intl.formatMessage({id: 'quotes.page.toast.unableUpdateFilter', defaultMessage: 'Unable to update filter.'}))
      return null
    }

    await refetchPolicyFilters()
    toast.success(intl.formatMessage({id: 'quotes.page.toast.filterUpdated', defaultMessage: 'Filter updated.'}))

    if (!payload.policyFilter) return null
    return {
      id: payload.policyFilter.id,
      name: payload.policyFilter.name,
      filters: parsePolicyFilterPayload(payload.policyFilter.filters),
      updated: payload.policyFilter.updated,
    } as PolicySavedFilter
  }

  const handleDeletePolicyFilter = async (id: string) => {
    const result = await gqlDeletePolicyFilter({
      variables: {id: parseId(id)},
    })

    const payload = result.data?.deletePolicyFilter
    if (!payload?.success) {
      handlePolicyFilterErrors(payload?.errors, intl.formatMessage({id: 'quotes.page.toast.unableDeleteFilter', defaultMessage: 'Unable to delete filter.'}))
      return false
    }

    await refetchPolicyFilters()
    toast.success(intl.formatMessage({id: 'quotes.page.toast.filterDeleted', defaultMessage: 'Filter deleted.'}))
    return true
  }

  const handleFilters = (override?: PolicyFilters) => {
    const data = override ?? formData
    const variables = cleanVariables({
      ...data,
      first: first,
      offset: (currentPage - 1) * first,
      holderName: searchByMembers ? null : data.policyName,
      memberName: searchByMembers ? data.policyName : null,
      insuranceCategory: data.insuranceCategory || null,
      effectiveYear: selectedYears.length > 0 ? selectedYears : null,
      orderBy: sortColumn ? columnToBackendMap[sortColumn] : null,
      orderDirection: sortColumn ? sortDirection : null,
    })

    gqlGetPolicies({variables})
  }

  // Componente para encabezados ordenables
  const SortableHeader = ({column, children}: {column: SortColumn; children: React.ReactNode}) => {
    const isActive = sortColumn === column
    const handleSort = () => {
      if (isActive) {
        // Si ya está ordenado por esta columna, invertir la dirección
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
      } else {
        // Nueva columna, empezar en ascendente
        setSortColumn(column)
        setSortDirection('asc')
      }
    }

    return (
      <th className='fw-bold' style={{cursor: 'pointer', userSelect: 'none'}} onClick={handleSort}>
        <div className='d-flex align-items-center gap-1'>
          {children}
          <div className='d-flex flex-column' style={{fontSize: '0.6rem', lineHeight: '0.5'}}>
            <i
              className='bi bi-caret-up-fill'
              style={{
                color: isActive && sortDirection === 'asc' ? '#3e97ff' : '#c4c4c4',
                marginBottom: '-2px',
              }}
            />
            <i
              className='bi bi-caret-down-fill'
              style={{
                color: isActive && sortDirection === 'desc' ? '#3e97ff' : '#c4c4c4',
                marginTop: '-2px',
              }}
            />
          </div>
        </div>
      </th>
    )
  }

  const handleFilterApply = (filters: any) => {
    const transformedFilters = {...filters}

    if (filters.signatureStatus === 'SIGNED') {
      transformedFilters.hasSignedEntry = true
    } else if (filters.signatureStatus === 'NOT_SIGNED') {
      transformedFilters.hasSignedEntry = false
    }

    delete transformedFilters.signatureStatus

    const newFormData = {
      ...formData,
      ...transformedFilters,
    }

    setFormData(newFormData)
    setCurrentPage(1) // Resetear a la página 1 cuando se aplican filtros

    const variables = cleanVariables({
      ...newFormData, // ¡arrays actualizados!
      first: first,
      offset: 0, // Siempre empezar desde la primera página
      holderName: newFormData.policyName || null,
      insuranceCategory: newFormData.insuranceCategory || null,
    })

    gqlGetPolicies({variables})
  }

  useEffect(() => {
    handleFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, gqlGetPolicies, currentPage, offset, sortColumn, sortDirection, selectedYears])

  useEffect(() => {
    if (!dataAllQuotes) return
    const {edges} = dataAllQuotes.allQuotes
    // if (isPag) {
    setDataArr((p) => [...edges])
    // } else {
    //   setDataArr(edges)
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAllQuotes])

  useEffect(() => {
    if (error) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const clearFilters = () => {
    const next = { ...INITIAL_POLICY_FILTERS }
    setFormData(next)
    setCurrentPage(1) // Resetear a la página 1 cuando se limpian filtros
    handleFilters(next)
  }

  const handleRemoveFilter = (filterKey: string | string[]) => {
    const next = { ...formData }

    // Si es un array de keys (para rangos de fechas), resetear todas
    const keysToReset = Array.isArray(filterKey) ? filterKey : [filterKey]

    keysToReset.forEach((key) => {
      // Resetear el valor del filtro según su tipo
      if (Array.isArray(next[key])) {
        next[key] = []
      } else if (typeof next[key] === 'boolean') {
        next[key] = undefined
      } else {
        next[key] = ''
      }
    })

    setFormData(next)
    setCurrentPage(1) // Resetear a la página 1 cuando se remueve un filtro
    handleFilters(next)
  }

  const handleUpdateQuote = async (id: string, data: any) => {
    gqlUpdateQuote({
      variables: {
        id: parseId(id),
        policyData: {
          archivedDate: data.archivedDate,
        },
      },
    })
  }

  useEffect(() => {
    if (!dataUpdate) return
    if (dataUpdate.updatePolicy?.success) {
      toast.success(intl.formatMessage({id: 'quotes.page.toast.quoteUpdated', defaultMessage: 'Quote updated successfully'}))
      handleFilters()
    } else if (dataUpdate.updatePolicy?.errors?.length > 0) {
      toast.error(intl.formatMessage(
        {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
        {message: dataUpdate.updatePolicy.errors[0]?.message || intl.formatMessage({id: 'quotes.add.toast.somethingWrong', defaultMessage: 'Something wrong'})}
      ))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  // if ((loading || !dataAllPolicies) && !isPag) {
  //   return <LoadingQuotes />
  // }

  const carrierOptions = filterData.carriers.map((c) => ({value: c.id, label: c.name}))
  const userOptions = filterData.users.map((u) => ({value: u.id, label: u.fullName}))
  const agentsOptions = filterData.agents.map((u) => ({
    value: parseId(u.id),
    label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
    fullName: `${u.firstName} ${u.lastName}`,
  }))
  const formOptions = filterData.signatureForms.map((f) => ({value: f.id, label: f.name}))
  const statusOptions = filterData.policyStatuses.map((s) => ({value: s.code, label: s.title}))
  // Enums del frontend: la etiqueta se traduce, el valor ingles sigue viajando
  // a la API. Salian en crudo (slice 1.11.c.2.b).
  const immigrationOptions = translatedImmigrationStatuses(intl)
  const stateOptions = filterData.states.map((s) => ({
    value: s.code,
    label: `${s.name} (${s.code})`,
  }))
  const leadSourcesOptions = filterData.leadSources.map((s) => ({value: parseId(s.id), label: s.name}))
  const policyTypesOptions =
    filterData.policyTypes.flatMap((cat: any) =>
      cat.policyTypeSet?.edges?.map(({ node }: any) => ({
        value: parseId(node.id),
        label: `${cat.name} › ${node.typePolicy}`,
      })) || []
  )
  const signatureStatusOptions = [
    {value: 'SIGNED', label: intl.formatMessage({id: 'common.status.signed', defaultMessage: 'Signed'})},
    {value: 'NOT_SIGNED', label: intl.formatMessage({id: 'quotes.filters.status.notSigned', defaultMessage: 'Did not sign'})},
  ]
  const documentStatusOptions = translatedDocumentStatuses(intl)
  const paymentStatusOptions = translatedPaymentStatuses(intl)



  const filtersConfig = [
    {key: 'carrier', label: intl.formatMessage({id: 'quotes.filters.field.carrier', defaultMessage: 'Carrier'}), options: carrierOptions},
    {key: 'user', label: intl.formatMessage({id: 'common.field.user', defaultMessage: 'User'}), options: agentsOptions},
    {key: 'assignedTo', label: intl.formatMessage({id: 'quotes.filters.field.assignedTo', defaultMessage: 'Assigned To'}), options: userOptions},
    {key: 'signatureForm', label: intl.formatMessage({id: 'quotes.filters.field.signatureForm', defaultMessage: 'Signature Form'}), options: formOptions},
    {key: 'stateEU', label: intl.formatMessage({id: 'common.field.state', defaultMessage: 'State'}), options: stateOptions},
    {
      key: 'stateJurisdictionType',
      label: intl.formatMessage({id: 'quotes.filters.field.stateJurisdiction', defaultMessage: 'State Jurisdiction Type'}),
      options: [
        {value: 'FEDERAL', label: intl.formatMessage({id: 'quotes.filters.jurisdiction.federal', defaultMessage: 'Federal'})},
        {value: 'STATE', label: intl.formatMessage({id: 'common.field.state', defaultMessage: 'State'})},
      ],
    },
    {key: 'status', label: intl.formatMessage({id: 'quotes.filters.field.policyStatus', defaultMessage: 'Policy Status'}), options: statusOptions},
    {key: 'immigrationStatus', label: intl.formatMessage({id: 'quotes.filters.field.immigrationStatus', defaultMessage: 'Immigration Status'}), options: immigrationOptions},
    {key: 'signatureStatus', label: intl.formatMessage({id: 'quotes.filters.field.signatureStatus', defaultMessage: 'Signature Status'}), options: signatureStatusOptions},
    {key: 'policyType', label: intl.formatMessage({id: 'quotes.filters.field.policyType', defaultMessage: 'Policy Type'}), options: policyTypesOptions },
    {key: 'leadSource', label: intl.formatMessage({id: 'quotes.filters.field.source', defaultMessage: 'Source'}), options: leadSourcesOptions},
    {key: 'metalLevel', label: intl.formatMessage({id: 'quotes.filters.field.metalLevel', defaultMessage: 'Metal Level'}), options: translatedMetalLevels(intl)},
    {
      key: 'documentStatus',
      label: intl.formatMessage({id: 'quotes.filters.field.documentStatus', defaultMessage: 'Document Status'}),
      options: documentStatusOptions,
    },
    {
      key: 'paymentStatus',
      label: intl.formatMessage({id: 'quotes.filters.field.paymentStatus', defaultMessage: 'Payment Status'}),
      options: paymentStatusOptions,
    },

  ]
  return (
    <div>
      <header
        className='py-4 d-flex align-items-center justify-content-between'
        style={{width: '100%', marginTop: '-10px'}}
      >
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none' /* onClick={() => setShowAside(true)} */>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item className='fs-3'>
            <FormattedMessage id='quotes.page.breadcrumb' defaultMessage='Quotes' />
          </Breadcrumb.Item>
        </Breadcrumb>

        <div className='d-flex align-items-center gap-2'>
          {canExportQuote && (
            <Button
              disabled={loadingExport}
              className='btn-sm btn-outline btn-export'
              onClick={handleDownload}
            >
              {loadingExport ? (
                <Spinner animation='border' size='sm' />
              ) : (
                <i className='bi bi-download'></i>
              )}{space}
              {loadingExport
                ? <FormattedMessage id='quotes.page.exporting' defaultMessage='Exporting...' />
                : <FormattedMessage id='quotes.page.export' defaultMessage='Export' />}
            </Button>
          )}
          <Button size='sm' onClick={onOpen}>
            <i className='bi bi-plus fs-3' /> <FormattedMessage id='quotes.page.newQuote' defaultMessage='New Quote' />
          </Button>
        </div>
      </header>
      <div className='quotes-container'>
        <div className='title-header'>
          <h3>
            <FormattedMessage id='quotes.page.allQuotes' defaultMessage='All Quotes' />
          </h3>
          <div className='filter-by-years'>
            {dataArr.length > 0 && (
              <span className='fw-bold me-4'>
                <FormattedMessage id='quotes.page.effectiveYear' defaultMessage='Effective year:' />
              </span>
            )}
            {lastThreeYears &&
              lastThreeYears.length > 0 &&
              lastThreeYears.map((year) => (
                <Form.Check
                  key={year}
                  inline
                  label={year}
                  checked={selectedYears.includes(year)}
                  onChange={() => {
                    setCurrentPage(1) // Resetear a la página 1 cuando se cambia el año
                    setSelectedYears(
                      (prev) =>
                        prev.includes(year)
                          ? prev.filter((y) => y !== year) // Quita si ya estaba
                          : [...prev, year] // Agrega si no estaba
                    )
                  }}
                />
              ))}
          </div>
        </div>
        <div className='my-4'>
          <InputGroup>
            <Form.Control
              placeholder={intl.formatMessage({id: 'quotes.page.searchPlaceholder', defaultMessage: 'Type your query here...'})}
              aria-label={intl.formatMessage({id: 'quotes.page.searchAriaLabel', defaultMessage: "Recipient's username with two button addons"})}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  policyName: e.target.value,
                }))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault() // evita el submit automático
                  setCurrentPage(1) // Resetear a la página 1 cuando se presiona Enter
                  handleFilters() // si tienes una función como esa
                }
              }}
              value={formData.policyName}
            />
            <Button
              onClick={() => {
                setCurrentPage(1) // Resetear a la página 1 cuando se hace clic en Search
                handleFilters()
              }}
              variant='outline-success'
              style={{border: '1px solid #dbdfe9'}}
            >
              <FormattedMessage id='common.action.search' defaultMessage='Search' />
            </Button>
            <Button
              variant='outline-primary'
              style={{border: '1px solid #dbdfe9'}}
              onClick={onOpenFilters}
            >
              <FormattedMessage id='common.action.filter' defaultMessage='Filter' />
            </Button>
          </InputGroup>
          <div className='line2'>
            <Form.Check
              type='checkbox'
              checked={searchByMembers}
              onChange={(e) => setSearchByMembers(e.target.checked)}
              label={
                <span>
                  <FormattedMessage id='quotes.page.searchByMembers' defaultMessage='Search by members:' />{space}
                  <span className='ms-2 ' data-tooltip-id='my-tooltip-info-1'>
                    <i className='bi bi-question-circle-fill text-primary' />
                  </span>
                  <Tooltip
                    id='my-tooltip-info-1'
                    place='bottom'
                    style={{
                      zIndex: 99999,
                      backgroundColor: 'white',
                      color: 'black',
                      boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
                    }}
                  >
                    <div className='tooltip-tfi'>
                      <p style={{color: '#222'}}>
                        <FormattedMessage
                          id='quotes.page.searchByMembersTooltip'
                          defaultMessage='Check this box if you need to search for policies by the name of the members.'
                        />
                      </p>
                    </div>
                  </Tooltip>
                </span>
              }
            />
            <AppliedFiltersList filters={formData} config={filtersConfig} clearFilters={clearFilters} onRemoveFilter={handleRemoveFilter}/>
          </div>
        </div>
        <div className='results-header'>
          <PaginationSection
            first={first}
            setFirst={(n) => {
              setFirst(n)
              setPage(1)
              setOffset(0)
            }}
            currentPage={currentPage}
            totalPages={totalPages}
            start={start}
            end={end}
            totalPolicies={totalContacts}
            updatePage={updatePage}
            PaginationUtils={PaginationUtils}
          />
          {/* <div className='pagination-container table-pagination-top'>
            <div className='pagination-entries'>
              <span style={{flex: 0}}>Show</span>
              <Form.Select
                value={first}
                onChange={(e) => {
                  setFirst(Number(e.target.value))
                  setCurrentPage(1)
                  setOffset(0)
                }}
                style={{flex: 0, flexBasis: '80px'}}
              >
                <option>10</option>
                <option>20</option>
                <option>30</option>
                <option>50</option>
              </Form.Select>
              <span style={{flex: 1}}> entries</span>
            </div>
            <div className='pagination-text'>
              Showing {start} to {end} of {totalPolicies} quotes
            </div>
            <div className='pagination-pages'>
              <Pagination size='sm'>
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => {
                    if (currentPage > 1) {
                      updatePage(currentPage - 1)
                    }
                  }}
                />
                <Pagination.Item
                  active={currentPage === 1}
                  onClick={() => {
                    if (currentPage !== 1) {
                      updatePage(1)
                    }
                  }}
                >
                  1
                </Pagination.Item>
                {currentPage > 3 && <Pagination.Ellipsis disabled />}
                {PaginationUtils.getVisiblePages(currentPage, totalPages)
                  .slice(1)
                  .map((page) => (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => {
                        if (page !== currentPage) {
                          updatePage(page)
                        }
                      }}
                    >
                      {page}
                    </Pagination.Item>
                  ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    if (currentPage < totalPages) {
                      updatePage(currentPage + 1)
                    }
                  }}
                />
              </Pagination>
            </div>
          </div> */}
          <div className='col-md-3'>
            <div className='results-filter'>
              <Col lg={12} md={12} sm={12} xs={12}></Col>
            </div>
          </div>
        </div>
        <div style={{width: '100%', overflowX: 'auto'}} className='mt-5'>
          <table
            className='table table-hover table-sm dataTable no-footer dtr-inline w-100'
            style={{whiteSpace: 'nowrap'}}
          >
            <thead>
              <tr role='row'>
                <th></th>
                <SortableHeader column='agent'><FormattedMessage id='common.field.agent' defaultMessage='Agent' /></SortableHeader>
                <SortableHeader column='client'><FormattedMessage id='quotes.list.col.client' defaultMessage='Client' /></SortableHeader>
                <SortableHeader column='carrier'><FormattedMessage id='quotes.list.col.policy' defaultMessage='Policy' /></SortableHeader>
                <SortableHeader column='created'><FormattedMessage id='common.field.created' defaultMessage='Created' /></SortableHeader>
                <SortableHeader column='assignedTo'><FormattedMessage id='quotes.list.col.assignedTo' defaultMessage='Assigned to' /></SortableHeader>
                <th className='fw-bold'>
                  <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 10 }).map((_, i) => (
                <tr key={i} className='placeholder-glow'>
                  <td>
                    <span className='placeholder rounded' style={{ width: 18, height: 18, display: 'inline-block' }} />
                  </td>
                  <td>
                    <span className='placeholder rounded-circle' style={{ width: 40, height: 40, display: 'inline-block' }} />
                  </td>
                  <td>
                    <span className='placeholder rounded' style={{ width: 130, display: 'inline-block' }} />
                    <br />
                    <span className='placeholder rounded mt-1' style={{ width: 90, display: 'inline-block' }} />
                  </td>
                  <td>
                    <span className='placeholder rounded' style={{ width: 110, display: 'inline-block' }} />
                    <br />
                    <span className='placeholder rounded mt-1' style={{ width: 130, display: 'inline-block' }} />
                    <br />
                    <span className='placeholder rounded mt-1' style={{ width: 55, height: 20, display: 'inline-block' }} />
                  </td>
                  <td>
                    <span className='placeholder rounded' style={{ width: 90, display: 'inline-block' }} />
                  </td>
                  <td>
                    <span className='placeholder rounded' style={{ width: 100, display: 'inline-block' }} />
                  </td>
                  <td>
                    <span className='placeholder rounded' style={{ width: 60, height: 28, display: 'inline-block' }} />
                  </td>
                </tr>
              ))}
              {!loading && dataArr.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className='d-flex flex-center' style={{height: '30vh'}}>
                      <FormattedMessage id='quotes.page.empty' defaultMessage='There are no quotes currently added' />
                    </div>
                  </td>
                </tr>
              )}
              {!loading &&
                dataArr.slice(first * page - first, first * page).map(({node: nodeQuote}) => (
                  <tr key={nodeQuote.id}>
                    <td />
                    <td>
                      <div>
                        <UserAvatar
                          user={nodeQuote.user}
                          tooltipId={`myt${nodeQuote.id}`}
                          size={45}
                        />
                        <AgentInfoTooltip id={`myt${nodeQuote.id}`} user={nodeQuote.user} />
                      </div>
                    </td>
                    <td>
                      <InfoClientPolicy
                        isQuote
                        id={nodeQuote.id}
                        policy={nodeQuote}
                        holder={nodeQuote.holderMember}
                      />
                    </td>
                    <td className='limit-width'>
                      <div className='info-policy-name'>
                        <span
                          className='text-primary text-link fw-bold'
                          style={{cursor: 'pointer'}}
                          data-tooltip-id={`myt2${parseId(nodeQuote.id)}`}
                        >
                          {nodeQuote.carrier?.name || notRecorded}
                        </span>
                        <p className='m-0 p-0' style={{fontSize: '0.95rem'}}>
                          <FormattedMessage
                            id='quotes.row.effective'
                            defaultMessage='Effective: {date}'
                            values={{date: moment(nodeQuote.effectiveDate).format('MMM DD, YYYY')}}
                          />
                        </p>
                        <div className='d-flex align-items-center gap-2 mt-1'>
                          <Badge
                            bg='primary'
                            className='d-flex align-items-center gap-1 text-white'
                            title={intl.formatMessage(
                              {id: 'quotes.row.applicantsTitle', defaultMessage: '{count, plural, one {# Applicant} other {# Applicants}}'},
                              {count: nodeQuote.applicants || 0}
                            )}
                          >
                            <i className='bi bi-people-fill' />
                            {nodeQuote.applicants || 0}
                          </Badge>
                          {nodeQuote.memberId && (
                            <span className='m-0 p-0' style={{fontSize: '0.95rem'}}>
                              <FormattedMessage id='quotes.row.memberId' defaultMessage='ID: {id}' values={{id: nodeQuote.memberId}} />
                            </span>
                          )}
                        </div>
                        <Tooltip
                          id={`myt2${parseId(nodeQuote.id)}`}
                          place='top'
                          style={{
                            zIndex: 99999,
                            backgroundColor: 'white',
                            color: 'black',
                            boxShadow:
                              'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
                          }}
                        >
                          <div className='tooltip-tfi' style={{color: '#222', maxWidth: '220px'}}>
                            <h6 className='text-center'>
                              <FormattedMessage id='quotes.row.moreInfo' defaultMessage='More information:' />
                            </h6>
                            <div className='gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='fw-bold m-0 p-0'>
                                <FormattedMessage id='quotes.row.product' defaultMessage='Product:' />
                              </p>
                              <p className='m-0 p-0'>
                                <FormattedMessage id='quotes.row.productQuote' defaultMessage='Quote' />
                              </p>
                            </div>
                            <div className='gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='fw-bold m-0 p-0'>
                                <FormattedMessage id='quotes.row.plan' defaultMessage='Plan:' />
                              </p>
                              <p className='m-0 p-0'>{nodeQuote.planName || notRecorded}</p>
                            </div>
                            <div className='gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='fw-bold m-0 p-0'>
                                <FormattedMessage id='quotes.row.metal' defaultMessage='Metal:' />
                              </p>
                              <p
                                className='m-0 p-0'
                                style={{maxWidth: '140px', wordBreak: 'break-all'}}
                              >
                                {metalLevelLabel(
                                  intl,
                                  nodeQuote.metalLevel,
                                  intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
                                )}
                              </p>
                            </div>
                            <div className='m-0 p-0 gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='p-0 m-0 fw-bold'>
                                <FormattedMessage id='quotes.row.premium' defaultMessage='Premium:' />
                              </p>
                              <p>{dollar}{nodeQuote.premium}</p>
                            </div>
                            <div className='m-0 p-0 gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='m-0 p-0 fw-bold'>
                                <FormattedMessage id='quotes.row.npn' defaultMessage='NPN:' />
                              </p>
                              <p>{nodeQuote.NPN}</p>
                            </div>
                            <div className='m-0 p-0 gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='m-0 p-0 fw-bold'>
                                <FormattedMessage id='quotes.row.blocked' defaultMessage='Blocked:' />
                              </p>
                              <p className='m-0 p-0'>
                                {notRecorded}
                              </p>
                            </div>
                            <div className='gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='m-0 p-0 fw-bold'>
                                <FormattedMessage id='quotes.row.isDuplicate' defaultMessage='is this a duplicate?' />
                              </p>
                              <p className='m-0 p-0'>
                                {notRecorded}
                              </p>
                            </div>
                            <div className='gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='m-0 p-0 fw-bold'>
                                <FormattedMessage id='quotes.row.lastDuplicate' defaultMessage='Last duplicate:' />
                              </p>
                              {/* <p>{node.duplicated?.display}</p> */}
                              <p className='m-0 p-0'></p>
                            </div>
                            <div className='gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='m-0 p-0 fw-bold'>
                                <FormattedMessage id='quotes.row.submitted' defaultMessage='Submitted:' />
                              </p>
                              <p className='m-0 p-0'>
                                {moment(nodeQuote.created).format('DD MMM, YY')}
                              </p>
                            </div>
                            <div className='gap-2 d-flex align-items-center justify-content-between mb-3'>
                              <p className='fw-bold m-0 p-0'>
                                <FormattedMessage id='quotes.row.lastUpdate' defaultMessage='Last update:' />
                              </p>
                              <p className='m-0 p-0'>
                                {moment(nodeQuote.updated).format('DD MMM, YY')}
                              </p>
                            </div>
                          </div>
                        </Tooltip>
                      </div>
                    </td>
                    <td>{moment(nodeQuote.created).format('DD MMM, YY hh:mm A')}</td>
                    <td>
                      <div style={{
                        maxWidth: '150px',
                        minWidth: '120px',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        lineHeight: '1.3'
                      }}>
                        {nodeQuote.assignedTo?.firstName} {nodeQuote.assignedTo?.lastName}
                      </div>
                    </td>
                    <td>
                      <Dropdown as={ButtonGroup}>
                        <Button
                          variant='outline-primary'
                          size='sm'
                          style={{border: '1px solid #3e97ff'}}
                          onClick={() => {
                            onOpenPreview()
                            setInfoPreview(nodeQuote)
                          }}
                        >
                          <FormattedMessage id='common.action.preview' defaultMessage='Preview' />
                        </Button>
                        <Dropdown.Toggle
                          split
                          variant='outline-primary'
                          size='sm'
                          id='dropdown-custom-2'
                          style={{border: '1px solid #3e97ff'}}
                        />
                        <Dropdown.Menu className='dropdown-menu-policy'>
                          <Dropdown.Item
                            onClick={() => {
                              setInfoPreview(nodeQuote)
                              onOpenAssignPolicy()
                            }}
                          >
                            <i className='bi bi-person-check' /> <FormattedMessage id='quotes.row.assignQuote' defaultMessage='Assign quote' />
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setInfoPreview(nodeQuote)
                              onOpenNotes()
                            }}
                          >
                            <i className='bi bi-pencil-square' /> <FormattedMessage id='quotes.row.addNote' defaultMessage='Add Note' />
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setInfoPreview(nodeQuote)
                              onOpenLogs()
                            }}
                          >
                            <i className='bi bi-list-columns-reverse' /> <FormattedMessage id='quotes.row.viewLogs' defaultMessage='View Logs' />
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          {nodeQuote.archivedDate === null ? (
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation()
                                const archivedDateStr = new Date().toISOString()
                                nodeQuote.archivedDate = archivedDateStr
                                handleUpdateQuote(nodeQuote.id, {
                                  archivedDate: archivedDateStr,
                                })
                              }}
                            >
                              <i className='bi bi-folder-fill' /> <FormattedMessage id='quotes.row.markArchived' defaultMessage='Mark as archived' />
                            </Dropdown.Item>
                          ) : (
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation()
                                nodeQuote.archivedDate = ''
                                handleUpdateQuote(nodeQuote.id, {
                                  archivedDate: null,
                                })
                              }}
                            >
                              <i className='bi bi-folder-fill' /> <FormattedMessage id='quotes.row.markUnarchived' defaultMessage='Mark as unarchived' />
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {isOpen && (
        <ModalAddPolice
          refetch={refetch}
          isOpen={isOpen}
          onClose={onClose}
          keyboard={false}
          backdrop='static'
          isQuote={true}
        />
      )}
      {isOpenPreview && infoPreview && (
        <ModalPreview id={infoPreview.id} isOpen={isOpenPreview} onClose={onClosePreview} isQuote />
      )}

      {isOpenFilters && (
        <ModalFilters
          isOpen={isOpenFilters}
          onClose={onCloseFilters}
          onApply={handleFilterApply}
          savedFilters={savedPolicyFilters}
          onSaveFilter={handleSavePolicyFilter}
          onUpdateFilter={handleUpdatePolicyFilter}
          onDeleteFilter={handleDeletePolicyFilter}
          setFilters={setFormData}
          filters={formData}
          users={filterData.users}
          agents={filterData.agents}
          states={filterData.states}
          immigrationStatuses={filterData.immigrationStatuses}
          signatureForms={filterData.signatureForms}
          carriers={filterData.carriers}
          policyStatuses={filterData.policyStatuses}
          documentStatuses={filterData.documentStatuses}
          paymentStatuses={filterData.paymentStatuses}
          policyTypes={filterData.policyTypes}
          leadSources={filterData.leadSources}
          isLoading={
            (filterLoadOptions.loadUsers && !dataUsers) ||
            (filterLoadOptions.loadAgents && !dataAgents) ||
            (filterLoadOptions.loadCarriers && !dataCarriers) ||
            (filterLoadOptions.loadStates && !dataStates) ||
            (filterLoadOptions.loadSignatureForms && !dataSignatureForms) ||
            (filterLoadOptions.loadPolicyTypes && !dataPolicyTypes) ||
            (filterLoadOptions.loadLeadSources && !dataLeadSources)
          }
        />
      )}

      {infoPreview && isOpenNotes && (
        <ModalNotes isOpen={isOpenNotes} onClose={onCloseNotes} idPolicy={String(parseId(infoPreview.id))} />
      )}
      {infoPreview && isOpenLogs && (
        <ModalViewLogs isOpen={isOpenLogs} onClose={onCloseLogs} id={infoPreview.id} />
      )}
      {infoPreview && isOpenAssignPolicy && (
        <ModalAssignAgentPolicy
          isOpen={isOpenAssignPolicy}
          onClose={onCloseAssignPolicy}
          id={infoPreview.id}
          refetch={refetch}
          typeAssign={'ASSIGN_TO'}
        />
      )}
    </div>
  )
}
